import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import MasterAdminDashboard from "./master-admin";
import CustomerAdminDashboard from "./customer-admin";
import StudentDashboard from "./student-dashboard";
import { MainContext } from "components/utils/Contexts";
import RootAdminDashboard from "./root-admin";

interface DashboardProps {
  ref?: any;
  headerRef: React.MutableRefObject<any>;
  subHeaderRef: React.MutableRefObject<any>;
}

const Dashboard: React.FC<DashboardProps> = forwardRef(
  ({ headerRef, subHeaderRef }, ref) => {
    // References definition
    const rootAdminDashboardRef: React.Ref<any> = useRef(null);
    const masterAdminDashboardRef: React.Ref<any> = useRef(null);
    const customerAdminDashboardRef: React.Ref<any> = useRef(null);
    const studentDashboardRef: React.Ref<any> = useRef(null);

    // Get the values from the context
    const { userRole, userSchool } = useContext(MainContext);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      setCreateNewStudent(bool: boolean) {
        if (customerAdminDashboardRef.current)
          customerAdminDashboardRef.current.setCreateNewStudent(bool);
      },
      setShowCreateNewClass(bool: boolean) {
        if (customerAdminDashboardRef.current)
          customerAdminDashboardRef.current.setShowCreateNewClass(bool);
      },
      pressBackButtonSubHeader() {
        if (customerAdminDashboardRef.current)
          (customerAdminDashboardRef.current as any).onBackButtonSubHeader();
      },
      setCustomerAdminSearchQuery(searchQuery: string) {
        if (customerAdminDashboardRef.current)
          (customerAdminDashboardRef.current as any).setSearchQuery(
            searchQuery
          );
      },
      onCreateClass() {
        let currentHandler: any;
        if (customerAdminDashboardRef.current)
          currentHandler = customerAdminDashboardRef.current;
        currentHandler.onCreateClass();
      },
      onCreateEditStudent(title: string, description: string) {
        if (customerAdminDashboardRef.current)
          customerAdminDashboardRef.current.onCreateEditStudent(
            title,
            description
          );
      },
      onCreateEditSchool(title: string, description: string) {
        if (masterAdminDashboardRef.current)
          masterAdminDashboardRef.current.onCreateEditSchool(
            title,
            description
          );
      },
      onCreateEditBrand(title: string, description: string) {
        if (rootAdminDashboardRef.current)
          rootAdminDashboardRef.current.onCreateEditBrand(title, description);
      },
      onDiscard() {
        if (userRole === "root admin" && rootAdminDashboardRef.current)
          rootAdminDashboardRef.current.onDiscard();
        else if (userRole === "master admin" && masterAdminDashboardRef.current)
          masterAdminDashboardRef.current.onDiscard();
        else if (
          ["customer admin", "class admin"].includes(userRole) &&
          customerAdminDashboardRef.current
        )
          customerAdminDashboardRef.current.onDiscard();
      },
      pressSaveButton() {
        if (userRole === "master admin" && masterAdminDashboardRef.current) {
          masterAdminDashboardRef.current.handleSave();
        } else if (
          ["customer admin", "class admin"].includes(userRole) &&
          customerAdminDashboardRef.current
        ) {
          customerAdminDashboardRef.current.handleSaveNewStudent();
        }
      },
      generateReport(format: "csv" | "pdf") {
        if (customerAdminDashboardRef.current)
          customerAdminDashboardRef.current.generateReport(format);
      },
      onBackButtonClick() {
        if (userRole === "root admin" && rootAdminDashboardRef.current) {
          rootAdminDashboardRef.current.onBackButtonClick();
        } else if (
          userRole === "master admin" &&
          masterAdminDashboardRef.current
        ) {
          masterAdminDashboardRef.current.onBackButtonClick();
        } else if (
          ["customer admin", "class admin"].includes(userRole) &&
          customerAdminDashboardRef.current
        ) {
          customerAdminDashboardRef.current.onBackButtonClick();
        }
      },
    }));

    /**
     * Ensure to close the back button on starting
     */
    useEffect(() => {
      headerRef.current.setHeaderBackButton(false);
    }, []);

    /**
     * Function to render the content
     */
    const renderContent = () => {
      switch (userRole) {
        case "root admin":
          return (
            <RootAdminDashboard
              ref={rootAdminDashboardRef}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          );
        case "master admin":
          return (
            <MasterAdminDashboard
              ref={masterAdminDashboardRef}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
              customerAdminDashboardRef={customerAdminDashboardRef}
            />
          );
        case "customer admin":
          return (
            <CustomerAdminDashboard
              ref={customerAdminDashboardRef}
              userSchool={userSchool}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          );
        case "class admin":
          return (
            <CustomerAdminDashboard
              ref={customerAdminDashboardRef}
              userSchool={userSchool}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          );
        case "student":
          return (
            <StudentDashboard ref={studentDashboardRef} headerRef={headerRef} />
          );
        default:
          return <></>;
      }
    };

    return renderContent();
  }
);

export default Dashboard;
